import { useState } from "react";
import { Calendar as CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";

/* v3.2.4 · Date picker — replaces every `<input type="date">` in the
   app with the shadcn Calendar + Popover pattern so users click a
   date instead of typing "YYYY-MM-DD".

   Props:
     value      — "YYYY-MM-DD" string or ""
     onChange   — (isoStringOrEmpty) => void
     placeholder — label shown when empty
     testid     — data-testid on the trigger button
     minDate/maxDate — optional Date instances (react-day-picker props) */
export default function DatePicker({
  value, onChange, placeholder = "Pick a date",
  testid = "date-picker", minDate, maxDate, disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = value ? _parseIso(value) : undefined;
  const label    = value || placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            "w-full px-3 py-2 text-left text-xs font-mono border border-white/10 bg-black/40 flex items-center justify-between gap-2 hover:border-[#00E5FF]/40 disabled:opacity-50",
            !value && "text-gray-500"
          )}
          data-testid={testid}
        >
          <span className="truncate">{label}</span>
          <CalendarIcon className="h-3.5 w-3.5 text-[#00E5FF]" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 bg-[#0A0C10] border-white/10" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={(d) => {
            onChange(d ? _toIso(d) : "");
            setOpen(false);
          }}
          fromDate={minDate}
          toDate={maxDate}
          initialFocus
        />
        {value && (
          <button type="button" onClick={() => { onChange(""); setOpen(false); }}
            className="w-full px-2 py-1.5 text-[10px] font-mono text-gray-400 hover:text-[#FF3B30] border-t border-white/10"
            data-testid={`${testid}-clear`}>
            clear
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}

function _parseIso(s) {
  const [y, m, d] = s.split("-").map(Number);
  if (!y || !m || !d) return undefined;
  // Use LOCAL midnight, not UTC. react-day-picker renders in local time,
  // and _toIso reads getFullYear/Month/Date (local). Using UTC here caused
  // an off-by-one in UTC-negative timezones (e.g. America/Los_Angeles).
  return new Date(y, m - 1, d);
}
function _toIso(dt) {
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, "0");
  const d = String(dt.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
